import { useMemo, useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, Printer, Download, FileDown, Sparkles, Lightbulb, ListChecks, CheckCircle2, List, PenLine, Mic, Square, Loader2, Volume2 } from "lucide-react";
import PdfPreview from "@/components/PdfPreview";
import { pdfjs } from "react-pdf";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useNavigate } from "react-router-dom";
import { mapProfileToFields, isProfileEmpty, mapAttorneyToFields } from "@/lib/profileAutofill";
import { humanizeLabel } from "@/lib/humanize";
import { maskPhone, isPhoneField } from "@/lib/format";
import { getCountyPref } from "@/lib/countyPref";
import SignaturePad from "@/components/SignaturePad";
import { EmailCopyDialog } from "@/components/EmailCopyDialog";
import { useAuth } from "@/context/AuthContext";
import { LegalDisclaimer } from "@/components/LegalDisclaimer";
import { PenTool } from "lucide-react";
import { UserRound, Lock, Unlock, Eraser, Trash2, EyeOff, Type, ExternalLink, History, X, Mail } from "lucide-react";

const LANG_LABEL = { en: "English", es: "Español", vi: "Tiếng Việt" };

// Date helpers: everything is displayed/stored as mm/dd/yyyy
const isoToMMDDYYYY = (val) => {
  if (typeof val !== "string") return val;
  const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : val;
};
const maskDate = (val) => {
  const d = String(val || "").replace(/\D/g, "").slice(0, 8);
  if (d.length > 4) return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
  if (d.length > 2) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return d;
};

export default function FormEditor({ form, initialValues = {}, submissionId, onSaved, preview = false, resumedDraft = false }) {
  const [values, setValues] = useState(initialValues);
  const { user } = useAuth();
  const [showResumed, setShowResumed] = useState(resumedDraft);
  const startFresh = () => {
    setValues({});
    try { localStorage.removeItem(`nc_draft_${form.number}`); } catch { /* ignore */ }
    setShowResumed(false);
    toast.success("Cleared — starting with a blank form.");
  };
  const [guidance, setGuidance] = useState({});
  const [saving, setSaving] = useState(false);
  const [dl, setDl] = useState(false);
  const [lang, setLang] = useState("en");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiDesc, setAiDesc] = useState(() => sessionStorage.getItem("finderDesc") || "");
  useEffect(() => { sessionStorage.removeItem("finderDesc"); }, []);
  // Normalize any stored ISO dates (yyyy-mm-dd) to mm/dd/yyyy for date fields
  useEffect(() => {
    const dateNames = form.fields.filter((f) => f.type === "date").map((f) => f.name);
    if (!dateNames.length) return;
    setValues((v) => {
      let changed = false;
      const nv = { ...v };
      dateNames.forEach((n) => {
        const conv = isoToMMDDYYYY(nv[n]);
        if (conv !== nv[n]) { nv[n] = conv; changed = true; }
      });
      return changed ? nv : v;
    });
  }, [form]);
  // Persist a local draft so entries survive a guest checkout redirect / page reload.
  const draftKey = `nc_draft_${form.number}`;
  useEffect(() => {
    try {
      if (values && Object.values(values).some((v) => v !== "" && v !== false && v != null)) {
        localStorage.setItem(draftKey, JSON.stringify(values));
      }
    } catch { /* ignore quota */ }
  }, [values, draftKey]);
  const [fileOpen, setFileOpen] = useState(false);
  const [filing, setFiling] = useState(null);
  const [filingBusy, setFilingBusy] = useState(false);
  const [livePreview, setLivePreview] = useState(true);
  const [mode, setMode] = useState("fields"); // "fields" | "direct"
  const pdfRef = useRef(null);
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);

  useEffect(() => { api.get("/profile").then(({ data }) => { setProfile(data || {}); if (data?.signature && !signature) setSignature(data.signature); if (data?.initials && !initials) setInitials(data.initials); }).catch(() => setProfile({})); }, []);

  // Auto-fill the user's chosen default county on empty county fields (from the county pop-up).
  useEffect(() => {
    if (preview) return;
    const pref = getCountyPref();
    if (!pref?.autofill || !pref.county) return;
    setValues((v) => {
      let changed = false;
      const nv = { ...v };
      for (const f of form.fields) {
        if (f.type === "checkbox" || f.type === "select") continue;
        const key = `${f.name} ${f.label || ""}`.toLowerCase();
        if (key.includes("county") && !key.includes("birthplace")) {
          if (!nv[f.name] || String(nv[f.name]).trim() === "") { nv[f.name] = pref.county; changed = true; }
        }
      }
      return changed ? nv : v;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  // Attorney accounts: auto-fill attorney name, State Bar number, firm AND office address
  // (from user settings) on any attorney-context fields — the party's fields are untouched.
  useEffect(() => {
    if (preview || !profile?.is_attorney) return;
    const attorneyProfile = {
      bar_number: profile.bar_number, attorney_name: profile.attorney_name, firm: profile.firm,
      address_line: profile.address_line, city: profile.city, state: profile.state, zip_code: profile.zip_code,
      phone: profile.phone, email: profile.email, full_name: profile.full_name,
    };
    if (!Object.values(attorneyProfile).some((x) => String(x || "").trim())) return;
    setValues((v) => {
      const mapped = mapAttorneyToFields(form.fields, attorneyProfile, v);
      return Object.keys(mapped).length ? { ...v, ...mapped } : v;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, profile]);

  const gate = () => {
    if (!preview) return false;
    toast.message("Unlock this form to save, print & download a clean copy", {
      action: { label: "Unlock — $3.95", onClick: () => navigate(`/forms/${form.number}`) },
    });
    return true;
  };

  const useMyInfo = () => {
    if (isProfileEmpty(profile)) {
      toast.message("Set up your info once, then autofill any form", {
        action: { label: "Add my info", onClick: () => navigate("/profile") },
      });
      return;
    }
    const mapped = mapProfileToFields(form.fields, profile, values);
    const count = Object.keys(mapped).length;
    if (!count) { toast.message("No matching blank fields to fill from your info"); return; }
    setValues((s) => ({ ...s, ...mapped }));
    toast.success(`Filled ${count} field${count === 1 ? "" : "s"} from your info`);
  };

  const dateNames = useMemo(() => new Set(form.fields.filter((f) => f.type === "date").map((f) => f.name)), [form]);

  // Harvest values typed directly on the PDF (direct mode) and merge into state.
  const getEffectiveValues = async () => {
    if (mode !== "direct" || !pdfRef.current?.getEditedValues) return values;
    try {
      const edited = await pdfRef.current.getEditedValues();
      const norm = { ...edited };
      dateNames.forEach((n) => { if (norm[n] != null) norm[n] = isoToMMDDYYYY(norm[n]); });
      const merged = { ...values, ...norm };
      setValues(merged);
      return merged;
    } catch {
      return values;
    }
  };

  const switchMode = async (m) => {
    if (m === mode) return;
    if (mode === "direct") await getEffectiveValues(); // capture PDF edits before leaving
    setMode(m);
  };

  const openFiling = async () => {
    setFileOpen(true);
    if (filing) return;
    setFilingBusy(true);
    try {
      const { data } = await api.get(`/forms/${form.number}/filing-steps`);
      setFiling(data);
    } catch {
      toast.error("Could not load filing steps");
    } finally {
      setFilingBusy(false);
    }
  };
  const [aiBusy, setAiBusy] = useState(false);
  const [ttsBusy, setTtsBusy] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [ttsLang, setTtsLang] = useState("en");
  const [ttsVoice, setTtsVoice] = useState("sage");
  const audioRef = useRef(null);
  const readyAudioRef = useRef(null);

  const stopSpeaking = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    readyAudioRef.current = null;
    setSpeaking(false);
  };

  const speakFiling = async () => {
    if (speaking) { stopSpeaking(); return; }
    if (!filing) return;
    // If audio was already fetched but blocked by autoplay, play it now within this fresh user gesture.
    if (readyAudioRef.current) {
      const a = readyAudioRef.current;
      audioRef.current = a;
      try { await a.play(); setSpeaking(true); readyAudioRef.current = null; return; } catch { /* fall through to refetch */ }
    }
    const parts = [`How to file ${form.number}.`];
    (filing.steps || []).forEach((s, i) => parts.push(`Step ${i + 1}. ${s.title}. ${s.detail || ""}`));
    if (filing.tips?.length) parts.push("Tips. " + filing.tips.join(". "));
    const text = parts.join(" ").slice(0, 4096);
    setTtsBusy(true);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 120000);
    try {
      const res = await api.post("/ai/tts", { text, lang: ttsLang, voice: ttsVoice }, { responseType: "blob", signal: controller.signal });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audio.onended = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      audio.onerror = () => { setSpeaking(false); toast.error("Playback failed"); };
      audioRef.current = audio;
      try {
        await audio.play();
        setSpeaking(true);
      } catch {
        // Autoplay blocked (common on Safari/iOS after the fetch delay) — keep audio ready for a one-tap replay.
        readyAudioRef.current = audio;
        toast.message("Audio ready — tap Listen once more to play");
      }
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
        toast.error("Audio is taking too long — try a shorter section or try again");
      } else {
        toast.error("Could not generate audio");
      }
    } finally {
      clearTimeout(timer);
      setTtsBusy(false);
    }
  };
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone not supported in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size === 0) return;
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "speech.webm");
          const { data } = await api.post("/ai/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text) {
            setAiDesc((prev) => (prev ? prev.trim() + " " : "") + data.text);
            toast.success("Added your dictation");
          } else {
            toast.error("Didn't catch that — try again");
          }
        } catch {
          toast.error("Could not transcribe audio");
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };
  const toggleRecording = () => (recording ? stopRecording() : startRecording());

  const languages = form.languages || ["en"];

  const sections = useMemo(() => {
    const map = {};
    form.fields.forEach((f) => { (map[f.section] = map[f.section] || []).push(f); });
    return map;
  }, [form]);

  const set = (name, v) => setValues((s) => ({ ...s, [name]: v }));

  const save = async () => {
    if (gate()) return;
    setSaving(true);
    try {
      const eff = await getEffectiveValues();
      if (submissionId) {
        await api.put(`/submissions/${submissionId}`, { form_number: form.number, field_values: eff, title: form.title, signature, signature_offset: sigOffset, initials });
      } else {
        const { data } = await api.post("/submissions", { form_number: form.number, field_values: eff, title: form.title, signature, signature_offset: sigOffset, initials });
        onSaved?.(data.id);
      }
      toast.success("Form saved to your account");
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    setDl(true);
    try {
      const eff = await getEffectiveValues();
      const res = await api.post(`/forms/${form.number}/pdf?lang=${lang}&flatten=true`, { field_values: eff, signature, signature_offset: sigOffset, initials }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${form.number}${lang !== "en" ? "-" + lang : ""}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Completed PDF downloaded");
    } catch {
      toast.error("Could not generate PDF");
    } finally {
      setDl(false);
    }
  };

  const [pk, setPk] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const clearForm = () => {
    setValues({});
    setPreviewKey((k) => k + 1);
    toast.success("Form cleared");
  };
  const deleteForm = async () => {
    if (!submissionId) return;
    try {
      await api.delete(`/submissions/${submissionId}`);
      toast.success("Saved form deleted");
      navigate("/saved");
    } catch {
      toast.error("Could not delete form");
    }
  };
  const printFrameRef = useRef(null);
  const [printing, setPrinting] = useState(false);
  const [signature, setSignature] = useState(null);
  const [sigOpen, setSigOpen] = useState(false);
  const [initials, setInitials] = useState(null);
  const [iniOpen, setIniOpen] = useState(false);
  const NUDGE_KEY = `sig_nudge_${form.number}`;
  const [sigOffset, setSigOffset] = useState(() => { const v = parseInt(localStorage.getItem(`sig_nudge_${form.number}`) || "0", 10); return Number.isFinite(v) ? v : 0; });
  const nudge = (d) => setSigOffset((o) => { const n = Math.max(-60, Math.min(60, o + d)); localStorage.setItem(NUDGE_KEY, String(n)); return n; });
  // Print ONLY the official form (with the user's entries), scaled to fit the page.
  // We render each PDF page to an image and print with @page margin:0 + width:100%,
  // so the court form fills the sheet instead of being shrunk by the browser's PDF viewer.
  const printForm = async () => {
    if (gate()) return;
    setPrinting(true);
    try {
      const eff = await getEffectiveValues();
      const res = await api.post(`/forms/${form.number}/pdf?lang=${lang}&flatten=true`,
        { field_values: eff, signature, signature_offset: sigOffset, initials }, { responseType: "blob" });
      const buf = await res.data.arrayBuffer();
      const pdf = await pdfjs.getDocument({ data: buf }).promise;
      const pages = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
        const landscape = viewport.width > viewport.height;
        pages.push({ src: canvas.toDataURL("image/png"), landscape });
      }
      const imgs = pages
        .map((p) => `<div class="sheet ${p.landscape ? "l" : "p"}"><img class="pg" src="${p.src}" /></div>`)
        .join("");
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${form.number}</title>
        <style>
          @page p { size: letter portrait; margin: 0; }
          @page l { size: letter landscape; margin: 0; }
          html, body { margin: 0; padding: 0; }
          .sheet { box-sizing: border-box; display: flex; align-items: center; justify-content: center;
                   width: 100%; height: 100vh; padding: 4px; overflow: hidden; page-break-after: always; }
          .sheet:last-child { page-break-after: auto; }
          .sheet.p { page: p; }
          .sheet.l { page: l; }
          .sheet img.pg { max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; }
        </style></head><body>${imgs}</body></html>`;

      let iframe = printFrameRef.current;
      if (!iframe) {
        iframe = document.createElement("iframe");
        iframe.setAttribute("aria-hidden", "true");
        iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
        document.body.appendChild(iframe);
        printFrameRef.current = iframe;
      }
      iframe.onload = () => {
        setTimeout(() => {
          try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* noop */ }
        }, 350);
      };
      iframe.srcdoc = html;
    } catch {
      toast.error("Could not prepare the form for printing");
    } finally {
      setPrinting(false);
    }
  };
  const downloadPacket = async () => {
    if (gate()) return;
    setPk(true);
    try {
      const eff = await getEffectiveValues();
      const res = await api.post(`/forms/${form.number}/packet?lang=${lang}`, { field_values: eff }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${form.number}-filing-packet.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Filing packet downloaded — cover sheet + completed form");
    } catch {
      toast.error("Could not generate packet");
    } finally {
      setPk(false);
    }
  };

  const [mailing, setMailing] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const emailCopy = async (extra) => {
    setMailing(true);
    try {
      const eff = await getEffectiveValues();
      const res = await api.post(`/forms/${form.number}/email-copy?lang=${lang}`, { field_values: eff, signature, signature_offset: sigOffset, initials, ...extra });
      toast.success(`Sent to ${res.data.email}${res.data.recipient_email ? ` and ${res.data.recipient_email}` : ""}`);
      setMailOpen(false);
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send the email");
      return false;
    } finally {
      setMailing(false);
    }
  };

  const runAi = async () => {
    if (!aiDesc.trim()) { toast.error("Describe your situation first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post(`/forms/${form.number}/ai-complete`, { description: aiDesc, current_values: values });
      const count = Object.keys(data.values || {}).length;
      const dateNames = new Set(form.fields.filter((f) => f.type === "date").map((f) => f.name));
      const incoming = { ...(data.values || {}) };
      dateNames.forEach((n) => { if (incoming[n] != null) incoming[n] = isoToMMDDYYYY(incoming[n]); });
      setValues((s) => ({ ...s, ...incoming }));
      setGuidance(data.guidance || {});
      toast.success(count ? `AI filled ${count} field${count === 1 ? "" : "s"} — review before saving` : "AI added guidance; review the fields");
      setAiOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "AI could not complete the form");
    } finally {
      setAiBusy(false);
    }
  };

  const blankUrl = lang === "es" ? form.pdf_es_url : lang === "vi" ? form.pdf_vi_url : form.pdf_url;

  const renderField = (f) => {
    const v = values[f.name] ?? (f.type === "checkbox" ? false : "");
    if (f.type === "textarea")
      return <Textarea data-testid={`field-${f.name}`} value={v} onChange={(e) => set(f.name, e.target.value)} className="rounded-sm mt-1" rows={3} />;
    if (f.type === "checkbox")
      return <div className="flex items-center gap-2 mt-2"><Checkbox data-testid={`field-${f.name}`} checked={!!v} onCheckedChange={(c) => set(f.name, c)} /><span className="text-sm text-muted-foreground">Yes</span></div>;
    if (f.type === "select")
      return (
        <Select value={v} onValueChange={(val) => set(f.name, val)}>
          <SelectTrigger data-testid={`field-${f.name}`} className="rounded-sm mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>{f.options?.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      );
    if (f.type === "date")
      return <Input data-testid={`field-${f.name}`} type="text" inputMode="numeric" placeholder="mm/dd/yyyy" maxLength={10} value={v} onChange={(e) => set(f.name, maskDate(e.target.value))} className="rounded-sm mt-1" />;
    if (isPhoneField(f))
      return <Input data-testid={`field-${f.name}`} type="tel" inputMode="tel" placeholder="(###) ###-####" maxLength={14} value={maskPhone(v)} onChange={(e) => set(f.name, maskPhone(e.target.value))} className="rounded-sm mt-1" />;
    return <Input data-testid={`field-${f.name}`} type="text" value={v} onChange={(e) => set(f.name, e.target.value)} className="rounded-sm mt-1" />;
  };

  const display = (f) => {
    const v = values[f.name];
    if (f.type === "checkbox") return v ? "☑ Yes" : "☐ No";
    if (isPhoneField(f) && v) return maskPhone(v);
    return v || "________________";
  };

  const actionBar = (
    <div className="flex flex-wrap gap-3 items-center no-print">
      <Button onClick={() => switchMode(mode === "fields" ? "direct" : "fields")} data-testid="toggle-fields-btn" variant="outline" className="rounded-sm gap-2 border-2 border-black hover:bg-secondary">
        {mode === "fields" ? <><EyeOff size={16} /> Hide Form Fields</> : <><List size={16} /> Show Form Fields</>}
      </Button>
      <Button onClick={() => { if (!gate()) setAiOpen(true); }} data-testid="ai-assist-btn" className="rounded-sm gap-2 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground transition-colors">
        <Sparkles size={16} /> Complete with AI
      </Button>
      <Button onClick={useMyInfo} data-testid="use-my-info-btn" variant="outline" className="rounded-sm gap-2 hover:bg-secondary">
        <UserRound size={16} /> Use My Info
      </Button>
      <Button onClick={save} disabled={saving} data-testid="save-form-btn" className="rounded-sm gap-2 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
        <Save size={16} /> {saving ? "Saving…" : "Save Form"}
      </Button>
      {languages.length > 1 && (
        <Select value={lang} onValueChange={setLang}>
          <SelectTrigger data-testid="lang-select" className="rounded-sm w-[140px] h-9"><SelectValue /></SelectTrigger>
          <SelectContent>{languages.map((l) => <SelectItem key={l} value={l}>{LANG_LABEL[l] || l}</SelectItem>)}</SelectContent>
        </Select>
      )}
      <Button onClick={() => setSigOpen(true)} data-testid="signature-btn" variant="outline" className={`rounded-sm gap-2 hover:bg-secondary ${signature ? "border-emerald-600 text-emerald-700" : ""}`}>
        <PenTool size={16} /> {signature ? "Signature added ✓" : "Add Signature"}
      </Button>
      {signature && (
        <div className="inline-flex items-center gap-1 border-2 border-black rounded-sm h-9 px-1" data-testid="sig-nudge" title="Nudge the signature up or down to sit on the line">
          <span className="text-xs text-muted-foreground px-1">Nudge</span>
          <button onClick={() => nudge(4)} data-testid="sig-nudge-up" className="h-7 w-7 grid place-items-center hover:bg-secondary">▲</button>
          <span className="text-xs font-semibold w-9 text-center tabular-nums" data-testid="sig-nudge-val">{sigOffset > 0 ? `+${sigOffset}` : sigOffset}</span>
          <button onClick={() => nudge(-4)} data-testid="sig-nudge-down" className="h-7 w-7 grid place-items-center border-l-2 border-black hover:bg-secondary">▼</button>
        </div>
      )}
      <Button onClick={() => setIniOpen(true)} data-testid="initials-btn" variant="outline" className={`rounded-sm gap-2 hover:bg-secondary ${initials ? "border-emerald-600 text-emerald-700" : ""}`}>
        <Type size={16} /> {initials ? "Initials added ✓" : "Add Initials"}
      </Button>
      <a href="https://portal.ncbar.gov/verification/search.aspx" target="_blank" rel="noopener noreferrer" data-testid="find-attorney-btn">
        <Button variant="outline" className="rounded-sm gap-2 hover:bg-secondary">
          <ExternalLink size={16} /> Find an Attorney
        </Button>
      </a>
      <Button onClick={downloadPdf} disabled={dl} data-testid="download-filled-btn" variant="outline" className="rounded-sm gap-2 hover:bg-secondary">
        <Download size={16} /> {dl ? "Generating…" : "Download Completed PDF"}
      </Button>
      <Button onClick={printForm} disabled={printing} variant="outline" data-testid="print-form-btn" className="rounded-sm gap-2 hover:bg-secondary">
        <Printer size={16} /> {printing ? "Preparing…" : "Print"}
      </Button>
      <Button onClick={openFiling} data-testid="how-to-file-btn" variant="outline" className="rounded-sm gap-2 hover:bg-secondary">
        <ListChecks size={16} /> How To File
      </Button>
      <Button onClick={downloadPacket} disabled={pk} data-testid="filing-packet-btn" variant="outline" className="rounded-sm gap-2 hover:bg-secondary">
        <FileDown size={16} /> {pk ? "Building…" : "Filing Packet"}
      </Button>
      <Button onClick={() => { if (!gate()) setMailOpen(true); }} disabled={mailing} data-testid="email-copy-btn" variant="outline" className="rounded-sm gap-2 hover:bg-secondary">
        {mailing ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} {mailing ? "Sending…" : "Email Me a Copy"}
      </Button>
      {blankUrl && (
        <a href={blankUrl} target="_blank" rel="noreferrer" data-testid="download-blank-btn">
          <Button variant="ghost" className="rounded-sm gap-2"><FileDown size={16} /> Blank PDF</Button>
        </a>
      )}

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" data-testid="clear-form-btn" className="rounded-sm gap-2 text-muted-foreground hover:text-accent">
            <Eraser size={16} /> Clear Form
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all entries?</AlertDialogTitle>
            <AlertDialogDescription>This empties every field on {form.number}. Your saved copy isn't changed until you Save again. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-form-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={clearForm} data-testid="clear-form-confirm" className="bg-accent text-accent-foreground hover:bg-accent/90">Clear form</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {submissionId && !preview && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" data-testid="delete-form-btn" className="rounded-sm gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
              <Trash2 size={16} /> Delete Form
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this saved form?</AlertDialogTitle>
              <AlertDialogDescription>This permanently removes “{form.number} · {form.title}” from your saved forms. This can't be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-form-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={deleteForm} data-testid="delete-form-confirm" className="bg-red-600 text-white hover:bg-red-700">Delete form</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {showResumed && (
        <div className="no-print flex items-center justify-between gap-3 border-l-4 border-accent bg-accent/10 px-4 py-2.5 text-sm" data-testid="resumed-draft-note">
          <span className="flex items-center gap-2 text-foreground"><History size={15} className="text-accent shrink-0" /> We resumed your saved entries — pick up right where you left off.</span>
          <div className="flex items-center gap-3 shrink-0">
            <button onClick={startFresh} data-testid="resumed-draft-clear" className="text-xs font-semibold text-accent hover:underline">Start fresh</button>
            <button onClick={() => setShowResumed(false)} data-testid="resumed-draft-dismiss" className="text-muted-foreground hover:text-foreground"><X size={15} /></button>
          </div>
        </div>
      )}
      {preview && (
        <div className="no-print border-[3px] border-black bg-accent text-accent-foreground p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="preview-banner">
          <div className="flex items-center gap-3">
            <Lock size={18} className="shrink-0" />
            <p className="text-sm font-medium">Preview mode — try filling this form free. Your entries show <span className="underline">watermarked</span>. Unlock to save, print & download a clean, court-ready copy.</p>
          </div>
          <Button onClick={() => navigate(`/forms/${form.number}`)} data-testid="preview-unlock-btn"
            className="rounded-sm shrink-0 bg-accent-foreground text-accent hover:bg-white transition-colors gap-2">
            <Unlock size={15} /> Unlock — $3.95
          </Button>
        </div>
      )}
      {/* Mode toggle: guided fields vs. fill directly on the document */}
      <div className="flex flex-wrap items-center gap-3 no-print" data-testid="editor-mode-toggle">
        <div className="inline-flex border-2 border-black rounded-sm overflow-hidden">
          <button type="button" onClick={() => switchMode("fields")} data-testid="mode-fields-btn"
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors ${mode === "fields" ? "bg-primary text-primary-foreground" : "bg-white text-foreground hover:bg-secondary"}`}>
            <List size={16} /> Form Fields
          </button>
          <button type="button" onClick={() => switchMode("direct")} data-testid="mode-direct-btn"
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border-l-2 border-black transition-colors ${mode === "direct" ? "bg-primary text-primary-foreground" : "bg-white text-foreground hover:bg-secondary"}`}>
            <PenLine size={16} /> Fill on Document
          </button>
        </div>
        <span className="text-xs text-muted-foreground">
          {mode === "direct" ? "Type directly on the official court form — entries sync back to the fields." : "Fill using guided fields with a live document preview."}
        </span>
      </div>

      {actionBar}

      {mode === "fields" ? (
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Editor */}
        <div className="no-print space-y-8">
        {form.fields_source === "pdf" && (
          <p className="text-xs text-muted-foreground -mt-4 no-print">
            Fields extracted directly from the official {form.number} form. "Download Completed PDF" fills the real court PDF with your entries.
          </p>
        )}
        {form.fields_source === "generic" && (
          <p className="text-xs text-muted-foreground -mt-4 no-print">
            This form's PDF isn't machine-fillable. Capture your details below, then use "Download Completed PDF" for a formatted document.
          </p>
        )}

        {Object.entries(sections).map(([section, fields]) => (
          <div key={section}>
            <p className="overline text-accent mb-4 pb-2 border-b border-border">{section}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {fields.map((f) => (
                <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                  <Label className="text-xs">{humanizeLabel(f.label)}</Label>
                  {renderField(f)}
                  {guidance[f.name] && (
                    <p className="flex items-start gap-1 text-[0.7rem] text-accent mt-1" data-testid={`guidance-${f.name}`}>
                      <Lightbulb size={12} className="mt-0.5 shrink-0" /> {guidance[f.name]}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Bottom action bar: Save & Print */}
        <div className="flex flex-wrap gap-3 items-center pt-6 border-t-2 border-border no-print" data-testid="form-bottom-actions">
          <Button onClick={save} disabled={saving} data-testid="save-form-btn-bottom" className="rounded-sm gap-2 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
            <Save size={16} /> {saving ? "Saving…" : "Save Form"}
          </Button>
          <Button onClick={printForm} disabled={printing} variant="outline" data-testid="print-form-btn-bottom" className="rounded-sm gap-2 hover:bg-secondary">
            <Printer size={16} /> {printing ? "Preparing…" : "Print"}
          </Button>
          <Button onClick={downloadPdf} disabled={dl} data-testid="download-filled-btn-bottom" variant="outline" className="rounded-sm gap-2 hover:bg-secondary">
            <Download size={16} /> {dl ? "Generating…" : "Download Completed PDF"}
          </Button>
        </div>
      </div>

      {/* Original official court document — live filled preview */}
      <div className="lg:sticky lg:top-24 h-fit no-print" data-testid="original-document-panel">
        <div className="flex items-center justify-between mb-3">
          <p className="overline text-accent">Original Document · Live Preview</p>
          <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
            <Switch checked={livePreview} onCheckedChange={setLivePreview} data-testid="live-preview-toggle" />
            <span className="text-muted-foreground">Live preview {livePreview ? "on" : "off"}</span>
          </label>
        </div>
        <PdfPreview number={form.number} lang={lang} values={values} enabled={livePreview} />
        <p className="text-[0.7rem] text-muted-foreground mt-2">
          {livePreview
            ? `This is the official ${form.number} court form with your entries filled in. It updates as you type.`
            : `Live preview is off for speed. Use “Generate preview” to render the filled ${form.number} on demand.`}
        </p>
      </div>
      </div>
      ) : (
        <div className="no-print" data-testid="direct-edit-panel">
          <div className="max-w-4xl mx-auto">
            <p className="overline text-accent mb-3">Fill On Document · {form.number}</p>
            <PdfPreview key={previewKey} ref={pdfRef} interactive number={form.number} lang={lang} values={values} />
            <p className="text-[0.7rem] text-muted-foreground mt-2">Click any blank on the official form above and type directly. Your entries are captured automatically — use Save Form or Download Completed PDF when finished.</p>
          </div>
        </div>
      )}

      {/* Legacy hidden reconstruction — no longer used for printing (Print now outputs the official PDF only). */}
      <div id="print-area" aria-hidden className="no-print" style={{ position: "absolute", left: "-9999px", top: 0, width: "800px" }}>
        <div className="bg-white text-slate-900 p-8">
          <div className="text-center border-b-2 border-slate-900 pb-4 mb-6">
            <p className="text-[0.65rem] uppercase tracking-[0.2em]">State Of North Carolina</p>
            <p className="text-[0.65rem] uppercase tracking-[0.2em] font-semibold">In The General Court Of Justice</p>
            <h2 className="font-serif text-2xl mt-3 leading-tight">{form.title}</h2>
            <p className="text-xs font-mono mt-1">{form.number}{form.statute ? ` · ${form.statute}` : ""}</p>
          </div>
          {Object.entries(sections).map(([section, fields]) => (
            <div key={section} className="mb-5">
              <p className="text-[0.7rem] uppercase tracking-widest font-semibold text-slate-500 mb-2">{section}</p>
              <div className="space-y-2">
                {fields.map((f) => (
                  <div key={f.name} className="text-sm flex flex-col sm:flex-row sm:gap-2">
                    <span className="font-medium text-slate-600 sm:min-w-[45%]">{humanizeLabel(f.label)}:</span>
                    <span className="whitespace-pre-wrap break-words">{display(f)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <p className="text-[0.6rem] text-slate-400 mt-8 border-t border-slate-200 pt-3">
            Prepared with NC Legal Forms. This document reproduces the fields of the official {form.number} form for completion and printing.
          </p>
        </div>
      </div>

      {/* AI dialog */}
      <EmailCopyDialog open={mailOpen} onOpenChange={setMailOpen} onSend={emailCopy} busy={mailing} userEmail={user?.email} />
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2"><Sparkles size={20} className="text-accent" /> Complete with AI</DialogTitle>
            <DialogDescription>
              Describe your situation in plain language. The AI will map your details to the correct fields on <span className="font-medium">{form.number}</span> and add short guidance. Always review before filing.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Textarea data-testid="ai-description-input" value={aiDesc} onChange={(e) => setAiDesc(e.target.value)} rows={6}
              className="rounded-sm pr-14" placeholder="e.g. I'm filing in Wake County. The child is Emma Smith, born 03/03/2020. The mother is Jane Smith, the father is John Smith. Case number 24-CVD-9988." />
            <button type="button" onClick={toggleRecording} disabled={transcribing} data-testid="ai-mic-btn"
              title={recording ? "Stop dictation" : "Speak your answer"}
              className={`absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full transition-colors ${recording ? "bg-accent text-accent-foreground animate-pulse" : "bg-secondary hover:bg-primary hover:text-primary-foreground"}`}>
              {transcribing ? <Loader2 size={16} className="animate-spin" /> : recording ? <Square size={15} /> : <Mic size={16} />}
            </button>
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            {recording ? <span className="text-accent font-medium">● Listening… tap the square to stop.</span>
              : transcribing ? "Transcribing your voice…"
              : <><Mic size={12} /> Tap the mic to speak instead of typing.</>}
          </p>
          <p className="text-xs text-muted-foreground">The AI only uses what you write and leaves unknown fields blank. It does not provide legal advice.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={runAi} disabled={aiBusy} data-testid="ai-run-btn" className="rounded-sm gap-2 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground transition-colors">
              <Sparkles size={16} /> {aiBusy ? "Thinking…" : "Fill My Form"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* How to file dialog */}
      <Dialog open={fileOpen} onOpenChange={(o) => { setFileOpen(o); if (!o) stopSpeaking(); }}>
        <DialogContent className="rounded-sm max-w-lg max-h-[85vh] overflow-y-auto" data-testid="filing-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2"><ListChecks size={20} className="text-accent" /> How to file {form.number}</DialogTitle>
            <DialogDescription>Step-by-step guide to filing this form with the North Carolina courts.</DialogDescription>
          </DialogHeader>
          {filing && !filingBusy && (
            <div className="flex flex-wrap items-center gap-2" data-testid="tts-controls">
              <Select value={ttsLang} onValueChange={(v) => { stopSpeaking(); setTtsLang(v); }}>
                <SelectTrigger data-testid="tts-lang-select" className="rounded-sm w-[130px] h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Español</SelectItem>
                  <SelectItem value="vi">Tiếng Việt</SelectItem>
                </SelectContent>
              </Select>
              <Select value={ttsVoice} onValueChange={(v) => { stopSpeaking(); setTtsVoice(v); }}>
                <SelectTrigger data-testid="tts-voice-select" className="rounded-sm w-[120px] h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["sage", "alloy", "onyx", "nova", "shimmer", "coral"].map((v) => (
                    <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={speakFiling} disabled={ttsBusy} data-testid="listen-filing-btn" variant="outline"
                className="rounded-sm gap-2 hover:bg-secondary">
                {ttsBusy ? <Loader2 size={16} className="animate-spin" /> : speaking ? <Square size={15} /> : <Volume2 size={16} />}
                {ttsBusy ? "Generating…" : speaking ? "Stop" : "Listen"}
              </Button>
            </div>
          )}
          {filingBusy ? (
            <p className="text-sm text-muted-foreground py-6 text-center animate-pulse">Preparing your filing guide…</p>
          ) : filing ? (
            <div className="space-y-5">
              <ol className="space-y-4">
                {filing.steps?.map((s, i) => (
                  <li key={i} className="flex gap-3" data-testid={`filing-step-${i}`}>
                    <span className="grid place-items-center w-6 h-6 shrink-0 bg-primary text-primary-foreground text-xs font-semibold rounded-full">{i + 1}</span>
                    <div>
                      <p className="font-medium leading-tight">{s.title}</p>
                      {s.detail && <p className="text-sm text-muted-foreground mt-0.5">{s.detail}</p>}
                    </div>
                  </li>
                ))}
              </ol>
              {filing.tips?.length > 0 && (
                <div className="bg-secondary p-4 rounded-sm">
                  <p className="overline text-accent mb-2">Tips</p>
                  <ul className="space-y-1.5">
                    {filing.tips.map((t, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 size={15} className="text-accent mt-0.5 shrink-0" /> {t}</li>
                    ))}
                  </ul>
                </div>
              )}
              <LegalDisclaimer />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-6 text-center">No filing steps available.</p>
          )}
        </DialogContent>
      </Dialog>
      <SignaturePad open={sigOpen} onOpenChange={setSigOpen} onSave={setSignature} />
      <SignaturePad open={iniOpen} onOpenChange={setIniOpen} onSave={setInitials}
        title="Add your initials" saveEndpoint="/profile/initials" saveLabel="Initials" />
    </div>
  );
}
